import { Alert, Box, Chip, CircularProgress, Tab, Tabs } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Dialog } from 'Common/Supplyvue-ui'
import { SettingsPage } from 'Features/Projects/Scenarios/EditSettings/SettingsPage'
import { useSettingsContext } from 'Features/Projects/Scenarios/EditSettings/EditSettings.types'
import { useParams } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import {
  useTemplateOutputs,
  useFetchTemplateRunRateOutputs,
  useFetchTemplateSummaryOutputs,
  useTemplateComparison,
  useTemplateMethodComparison,
  invalidateTemplateResults,
} from 'Features/Projects/Scenarios/Templates/TemplateResults/TemplateResults.data'
import {
  hasRunningTemplate,
  useBaseSourceSiteWorkCentre,
  useGenerateTemplates,
  useUpdateTemplateGeneration,
} from 'Features/Projects/Scenarios/Templates/PlanTemplateSummary.data'
import {
  methodLabel,
  PreferredMethod,
  RUN_METHODS,
  staleTemplatesText,
} from 'Features/Projects/Scenarios/Templates/PlanTemplateSummary.types'
import { Loader } from 'Common/Loader/Loader'
import { useEffect, useRef, useState } from 'react'
import { DetailsView } from 'Features/Projects/Scenarios/Templates/TemplateResults/Details/Details.view'
import { RunRateView } from 'Features/Projects/Scenarios/Templates/TemplateResults/RunRate/Results.RunRate.view'
import { ComparisonView } from 'Features/Projects/Scenarios/Templates/TemplateResults/Comparison/Comparison.view'
import { MethodComparisonView } from 'Features/Projects/Scenarios/Templates/TemplateResults/Comparison/MethodComparison.view'
import { templateSummaryMetrics } from 'Features/Projects/Scenarios/Templates/TemplateResults/Results/Results.PlanTemplateSummary.view'
import { ErrorDisplay } from 'Common/Error/Error'
import { EmptyState } from 'Common/EmptyState/EmptyState'
import { KpiStrip, KpiTile } from 'Common/Components/KpiTile'
import { SegmentedControl } from 'Common/Components/SegmentedControl'
import { InsertChartOutlined } from '@mui/icons-material'

/** The five summary figures shown above a method's tabs, in this order. */
const HEADLINE = [
  'avgUtilisationHoursNonFOQPerWeek',
  'avgChangeOversPerWeek',
  'percentageChangeOver',
  'avgCycleSpeed',
  'utilisationVolatility',
] as const

type TabKey = 'methods' | 'details' | 'runRate' | 'comparison'

/**
 * One work centre's generated plan templates
 * (`…/template-summary/template-results/:workcentreId`).
 *
 * Every run generates a template per method, so the page opens on the method
 * comparison: the methods' summary figures, sequence of events and cycle
 * split side by side, where the user picks the preferred one, and each SKU
 * group's cycle per method. That replaced the one-method Results tab. The
 * other tabs — Details, Run rate and Vs current plan — show one method at a
 * time, chosen with the method switch above them (the preferred one first).
 *
 * The old URL with a method (`…/:workcentreId/:preferredTemplate/template`)
 * still works and opens that method's tabs.
 */
export const TemplateResultsView = () => {
  const { state } = useSettingsContext()
  const { projectId, scenarioId, workcentreId, preferredTemplate } = useParams<{
    projectId: string
    scenarioId: string
    workcentreId: string
    preferredTemplate?: PreferredMethod
  }>() as {
    projectId: string
    scenarioId: string
    workcentreId: string
    preferredTemplate?: PreferredMethod
  }

  const {
    data: comparison,
    isLoading: isComparisonLoading,
    isError: isComparisonError,
  } = useTemplateMethodComparison({ projectId, scenarioId, workcentreId })

  const completed = (comparison?.methods ?? [])
    .filter((m) => m.status === 'completed' && m.summary)
    .map((m) => m.templateMethod)
  const preferred = comparison?.workCentre.preferredTemplate ?? 'constrained'

  const [tab, setTab] = useState<TabKey>(
    preferredTemplate ? 'details' : 'methods'
  )
  const [picked, setPicked] = useState<string | null>(preferredTemplate ?? null)
  // The method the per-method tabs show: the one picked, else the preferred,
  // else the first that finished.
  const viewed = (
    picked && completed.includes(picked)
      ? picked
      : completed.includes(preferred)
        ? preferred
        : (completed[0] ?? preferred)
  ) as PreferredMethod

  const params = {
    projectId,
    scenarioId,
    workcentreId,
    preferredMethod: viewed,
  }
  const outputs = useTemplateOutputs(params)
  const vsPlan = useTemplateComparison(params)
  const runRate = useFetchTemplateRunRateOutputs(params)
  const summaryOutputs = useFetchTemplateSummaryOutputs(params)

  // Choosing the preferred method saves that one work centre's row.
  const { data: workCentres } = useBaseSourceSiteWorkCentre({
    projectId,
    scenarioId,
  })
  const { mutate: savePreferred, isPending: isSavingPreferred } =
    useUpdateTemplateGeneration(
      () => toast.success('Preferred method updated'),
      () => toast.error('Failed to update the preferred method')
    )
  const handleUseMethod = (method: string) => {
    const row = workCentres?.find(
      (item) => String(item.workCentreKey) === String(workcentreId)
    )
    if (!row) {
      toast.error('Work centre not found in the template summary')
      return
    }
    savePreferred({
      projectId,
      scenarioId,
      state: [{ ...row, preferredTemplate: method as PreferredMethod }],
    })
  }

  // Templates generated before this line's inputs last changed.
  const thisLine = workCentres?.find(
    (item) => String(item.workCentreKey) === String(workcentreId)
  )
  const staleMethods = thisLine?.staleMethods ?? []

  // Re-run this work centre: every method, as Generate on Template summary.
  // The summary query polls while a method is running; when the run ends,
  // this page's results are refetched.
  const queryClient = useQueryClient()
  const [isConfirmOpen, setIsConfirmOpen] = useState(false)
  const { mutate: generate, isPending: isStarting } = useGenerateTemplates(
    () =>
      toast.success(
        `Template generation started: ${RUN_METHODS.length} methods for this work centre`
      ),
    () => toast.error('Template generation could not be started')
  )
  const isRunning = !!thisLine && hasRunningTemplate([thisLine])
  const wasRunning = useRef(isRunning)
  useEffect(() => {
    if (wasRunning.current && !isRunning) invalidateTemplateResults(queryClient)
    wasRunning.current = isRunning
  }, [isRunning, queryClient])
  const handleGenerate = () => {
    setIsConfirmOpen(false)
    generate({
      projectId,
      scenarioId,
      workcentreIds: [Number(workcentreId)],
    })
  }

  const summary = summaryOutputs.data?.[0]
  const headline = summary
    ? templateSummaryMetrics(summary)
        .filter((item) => (HEADLINE as readonly string[]).includes(item.key))
        .sort(
          (a, b) =>
            HEADLINE.indexOf(a.key as (typeof HEADLINE)[number]) -
            HEADLINE.indexOf(b.key as (typeof HEADLINE)[number])
        )
    : []

  const workcentreCode = comparison?.workCentre.workcentreCode
  const subtitle = [
    state.selectedScenario,
    `Work centre ${workcentreCode ?? workcentreId}`,
  ]
    .filter(Boolean)
    .join(' · ')

  const methodTab = tab !== 'methods'
  const methodLoading =
    outputs.isLoading || summaryOutputs.isLoading || runRate.isLoading
  const methodError =
    outputs.isError || summaryOutputs.isError || runRate.isError

  return (
    <SettingsPage
      title="Template results"
      subtitle={subtitle}
      status={
        comparison && (
          <Chip
            label={`Preferred: ${methodLabel(preferred)}`}
            color="primary"
          />
        )
      }
      actions={
        <Button
          variant="contained"
          onClick={() => setIsConfirmOpen(true)}
          disabled={!thisLine || isRunning || isStarting}
          startIcon={
            isRunning || isStarting ? (
              <CircularProgress color="inherit" size={14} />
            ) : undefined
          }
        >
          {isRunning || isStarting ? 'Generating…' : 'Generate templates'}
        </Button>
      }
    >
      <Dialog
        open={isConfirmOpen}
        title="Generate templates for this work centre?"
        content={`Every method (${RUN_METHODS.map(methodLabel).join(', ')}) is generated again for work centre ${workcentreCode ?? workcentreId}. The new templates replace the current ones, including any changes saved on the Run rate tab.`}
        primaryButtonLabel="Generate templates"
        secondaryButtonLabel="Cancel"
        handleClose={() => setIsConfirmOpen(false)}
        handlePrimaryButtonClick={handleGenerate}
        handleSecondaryButtonClick={() => setIsConfirmOpen(false)}
      />
      {isComparisonLoading ? (
        <Loader />
      ) : isComparisonError || !comparison ? (
        <ErrorDisplay />
      ) : (
        <Box>
          {isRunning && (
            <Alert severity="info" sx={{ mb: 1.5 }}>
              Generating templates for this work centre. The results below
              update when the run finishes.
            </Alert>
          )}
          {thisLine && staleMethods.length > 0 && (
            <Alert severity="warning" sx={{ mb: 1.5 }}>
              {staleTemplatesText(thisLine, staleMethods)}
            </Alert>
          )}
          <Tabs
            value={tab}
            onChange={(_event, value: TabKey) => setTab(value)}
            aria-label="Template result views"
          >
            <Tab label="Method comparison" value="methods" />
            <Tab label="Details" value="details" disabled={!completed.length} />
            <Tab
              label="Run rate"
              value="runRate"
              disabled={!completed.length}
            />
            <Tab
              label="Vs current plan"
              value="comparison"
              disabled={!completed.length}
            />
          </Tabs>

          <Box
            role="tabpanel"
            aria-label={tab}
            sx={{
              pt: '12px',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
            }}
          >
            {tab === 'methods' && (
              <MethodComparisonView
                data={comparison}
                handleUseMethod={handleUseMethod}
                isSaving={isSavingPreferred}
              />
            )}

            {methodTab && completed.length === 0 && (
              <EmptyState
                icon={<InsertChartOutlined />}
                title="No template yet"
                text="Generate templates on the Template summary page; each method appears here when it finishes."
              />
            )}

            {methodTab && completed.length > 0 && (
              <>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}
                >
                  <SegmentedControl
                    label="Method"
                    value={viewed}
                    options={completed.map((method) => ({
                      value: method as PreferredMethod,
                      label:
                        methodLabel(method) +
                        (method === preferred ? ' · preferred' : ''),
                    }))}
                    onChange={setPicked}
                  />
                </Box>

                {methodLoading ? (
                  <Loader />
                ) : methodError ? (
                  <ErrorDisplay />
                ) : (
                  <>
                    {headline.length > 0 && (
                      <KpiStrip>
                        {headline.map((item) => (
                          <KpiTile
                            key={item.key}
                            label={item.label}
                            value={item.value}
                            unit={item.unit}
                          />
                        ))}
                      </KpiStrip>
                    )}
                    {tab === 'details' && (
                      <DetailsView
                        data={outputs.data ?? []}
                        isLoading={false}
                      />
                    )}
                    {tab === 'runRate' && (
                      <RunRateView
                        // Its edit state starts from the rows it mounts with.
                        key={viewed}
                        data={runRate.data ?? []}
                        isLoading={false}
                        method={viewed}
                      />
                    )}
                    {tab === 'comparison' &&
                      (vsPlan.isLoading ? (
                        <Loader />
                      ) : vsPlan.isError || !vsPlan.data ? (
                        <ErrorDisplay />
                      ) : (
                        <ComparisonView data={vsPlan.data} isLoading={false} />
                      ))}
                  </>
                )}
              </>
            )}
          </Box>
        </Box>
      )}
    </SettingsPage>
  )
}
